import React, { useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

/**
 * Confirmation dialog for the "reset all seat numbers" bulk action.
 *
 * The switch picks what happens to seated jurors when the custom numbering
 * is removed:
 *  - ON ("Keep jurors in their seats"): jurors stay in their chairs and take
 *    on the default number of that chair.
 *  - OFF ("Only change seat numbers"): jurors keep their current numbers and
 *    follow them to whichever seat carries that number by default.
 */
const ResetSeatNumbersDialog = ({ open, label, onConfirm, onClose }) => {
  const [keepJurorsSeated, setKeepJurorsSeated] = useState(true);

  // Fresh default on every open — the safer behavior is preselected.
  React.useEffect(() => {
    if (open) {
      setKeepJurorsSeated(true);
    }
  }, [open]);

  const handleConfirm = () => {
    onConfirm({ keepJurorsSeated });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      aria-labelledby="reset-seat-numbers-title"
    >
      <DialogTitle id="reset-seat-numbers-title">
        Reset all seat numbers?
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          All custom seat numbers in the {label} will return to the default
          numbering.
        </DialogContentText>
        <FormControlLabel
          sx={{ mt: 2 }}
          control={
            <Switch
              checked={keepJurorsSeated}
              onChange={(event) => setKeepJurorsSeated(event.target.checked)}
            />
          }
          label={keepJurorsSeated ? 'Keep jurors in their seats' : 'Only change seat numbers'}
        />
        <Typography variant="body2" color="textSecondary" sx={{ ml: 6 }}>
          {keepJurorsSeated
            ? 'Jurors stay where they are; each one takes on the default number of their seat.'
            : 'Jurors keep their current numbers and move to the seat that has that number by default.'}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button onClick={handleConfirm} color="primary" variant="contained" autoFocus>
          Reset Seat Numbers
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResetSeatNumbersDialog;
